/**
 * Re-skin the Shopify Forms app embed (<shopify-forms-embed>) to match the
 * native theme form.
 *
 * The app renders its form inside an OPEN shadow root that uses adopted
 * stylesheets and hashed class names, so external CSS (custom.css) cannot
 * reach it. Because the shadow root is open we inject a <style> element into
 * it via JS. CSS custom properties inherit across the shadow boundary, so the
 * theme's :root variables (set in snippets/head-css.liquid) resolve inside.
 *
 * Everything is !important because the app's own scoped styles have higher
 * specificity (hashed class + element) and may load after ours.
 */
(function () {
  'use strict';

  var SHADOW_CSS = [
    ':host { text-align: left !important; width: 100% !important; }',
    '',
    '/* The app caps its content narrow & left-aligned; let it fill the host. */',
    '[class*="container" i], [class*="inline" i], [class*="formContainer" i],',
    '[class*="gridItem" i], [class*="formFieldset" i], [data-sizing] {',
    '  max-width: 100% !important;',
    '  width: 100% !important;',
    '}',
    '',
    '/* Stack fields with the theme\'s field spacing (theme .field = 2.5rem) */',
    'form {',
    '  display: flex !important;',
    '  flex-direction: column !important;',
    '  gap: 2.5rem !important;',
    '  align-items: stretch !important;',
    '}',
    'form > * { margin-top: 0 !important; margin-bottom: 0 !important; }',
    '',
    '/* Field wrapper: stack label above input (app uses floating labels) */',
    '[class*="formFieldContainer" i] {',
    '  display: flex !important;',
    '  flex-direction: column !important;',
    '}',
    '',
    '/* The app overlays a ::before mask on top of textarea fields (to hide the',
    '   floating-label track). With static labels it just covers our label, so',
    '   remove it. */',
    '[class*="formFieldContainer" i]::before {',
    '  content: none !important;',
    '  display: none !important;',
    '  background: transparent !important;',
    '}',
    '',
    '/* Field labels: force the app\'s floating labels to static, above the field.',
    '   This matches the theme and stops them overlapping the input value',
    '   (e.g. "Telefonnummer" over "+49") and breaking the textarea top border. */',
    'label, [class*="formInputFieldLabel" i] {',
    '  display: block !important;',
    '  position: static !important;',
    '  order: -1 !important;',
    '  transform: none !important;',
    '  top: auto !important;',
    '  left: auto !important;',
    '  margin: 0 0 0.8rem !important;',
    '  padding: 0 !important;',
    '  line-height: normal !important;',
    '  font-size: calc(var(--font-body-scale, 1) * 1.5rem) !important;',
    '  font-weight: normal !important;',
    '  color: rgb(var(--color-base-text)) !important;',
    '  background: transparent !important;',
    '  text-align: left !important;',
    '  cursor: pointer !important;',
    '  width: auto !important;',
    '  height: auto !important;',
    '  pointer-events: auto !important;',
    '}',
    '',
    '/* Text inputs, textareas and selects */',
    'input[type="text"], input[type="email"], input[type="tel"],',
    'input[type="number"], input[type="url"], input[type="search"],',
    'input[type="password"], input[type="date"], input:not([type]),',
    'textarea, select {',
    '  font-size: calc(var(--font-body-scale, 1) * 1.4rem) !important;',
    '  font-family: inherit !important;',
    '  -webkit-appearance: none !important;',
    '  appearance: none !important;',
    '  background-color: rgb(var(--color-base-background-1)) !important;',
    '  color: rgb(var(--color-base-text)) !important;',
    '  padding: 0.5rem 1.5rem !important;',
    '  width: calc(100% - 5px) !important;',
    '  box-sizing: border-box !important;',
    '  border-radius: var(--inputs-radius, 2px) !important;',
    '  height: 4.2rem !important;',
    '  border: var(--inputs-border-width, 1px) solid rgb(var(--color-base-border)) !important;',
    '  box-shadow: 0 0.2rem 0 var(--color-box-shadow, rgba(0, 0, 0, 0.04)) !important;',
    '  transition: box-shadow 0.18s ease !important;',
    '  text-align: left !important;',
    '}',
    '',
    '/* International phone field: country selector sits inline with the number. */',
    '[class*="formPhoneInputContainer" i] {',
    '  display: flex !important;',
    '  align-items: flex-end !important;',
    '  gap: 0.4rem !important;',
    '}',
    '/* country selector should line up with the input, not the label above it */',
    '.phone-country-selector, [class*="selectContainer" i] { padding-bottom: 0.5rem !important; }',
    '.phone-country-selector,',
    '[class*="selectContainer" i] { flex: 0 0 auto !important; }',
    '',
    '/* Country selector is a bare toggle, not a theme button: no bg/shadow/border */',
    '.phone-country-selector button,',
    '[class*="selectToggle" i] {',
    '  background: none !important;',
    '  background-color: transparent !important;',
    '  box-shadow: none !important;',
    '  border: 0 !important;',
    '  border-radius: 0 !important;',
    '  padding: 0 0.4rem !important;',
    '  min-width: 0 !important;',
    '  width: auto !important;',
    '  height: auto !important;',
    '  color: inherit !important;',
    '}',
    '.phone-country-selector button:hover,',
    '[class*="selectToggle" i]:hover { box-shadow: none !important; }',
    '',
    '/* Phone number input fills the rest of the row */',
    '[class*="formPhoneInputContainer" i] [class*="formFieldContainer" i] {',
    '  flex: 1 1 auto !important;',
    '  min-width: 0 !important;',
    '}',
    '[class*="formPhoneInputField" i] { width: 100% !important; }',
    '',
    'textarea {',
    '  height: auto !important;',
    '  min-height: 12rem !important;',
    '  padding-top: 1rem !important;',
    '  padding-bottom: 1rem !important;',
    '}',
    '',
    '/* Theme focus ring */',
    'input:focus, textarea:focus, select:focus { outline: none !important; box-shadow: none !important; }',
    'input:hover, input:focus-visible,',
    'textarea:hover, textarea:focus-visible,',
    'select:hover, select:focus-visible {',
    '  outline: 0 !important;',
    '  box-shadow: 0 0 0 calc(0.1rem + var(--inputs-border-width, 1px)) rgb(var(--color-base-text)) !important;',
    '}',
    '',
    '/* Submit button → theme .button */',
    'button, input[type="submit"], [role="button"] {',
    '  font-family: var(--font-buttons-family, inherit) !important;',
    '  cursor: pointer !important;',
    '  display: inline-flex !important;',
    '  justify-content: center !important;',
    '  align-items: center !important;',
    '  border: 0 !important;',
    '  padding: 1.2rem 2.5rem !important;',
    '  text-decoration: none !important;',
    '  background-color: rgb(var(--color-buttons-primary-background, var(--color-base-accent-1))) !important;',
    '  box-shadow: 0 0 0 0.1rem rgb(var(--color-buttons-primary-background, var(--color-base-accent-1))) !important;',
    '  color: rgb(var(--color-buttons-primary-text, var(--color-base-accent-1-text))) !important;',
    '  min-width: 12rem !important;',
    '  width: auto !important;',
    '  height: auto !important;',
    '  font-size: calc(var(--font-body-scale, 1) * 1.5rem) !important;',
    '  line-height: calc(1 + 0.2 / var(--font-body-scale, 1)) !important;',
    '  border-radius: var(--buttons-radius, 2px) !important;',
    '  font-weight: var(--font-bolder-weight, 600) !important;',
    '  text-transform: var(--font-buttons-transform, none) !important;',
    '}',
    '',
    'button:hover, input[type="submit"]:hover, [role="button"]:hover {',
    '  box-shadow: 0 0 0 0.2rem rgb(var(--color-buttons-primary-background, var(--color-base-accent-1))) !important;',
    '}',
    '',
    '/* Validation / error text */',
    '[class*="error" i], [class*="Error"] {',
    '  color: var(--color-error) !important;',
    '}'
  ].join('\n');

  function applyTo(embed) {
    var root = embed.shadowRoot;
    if (!root) return false; // shadow root not attached yet
    if (root.querySelector('style[data-theme-form]')) return true; // already styled
    var style = document.createElement('style');
    style.setAttribute('data-theme-form', '');
    style.textContent = SHADOW_CSS;
    root.appendChild(style);
    return true;
  }

  function scan() {
    var embeds = document.querySelectorAll('shopify-forms-embed');
    if (!embeds.length) return false;
    var allDone = true;
    for (var i = 0; i < embeds.length; i++) {
      if (!applyTo(embeds[i])) allDone = false;
    }
    return allDone;
  }

  // shadowRoot attaches asynchronously and is NOT observable via MutationObserver,
  // so poll until every embed is styled (give up after ~30s).
  var attempts = 0;
  var poll = setInterval(function () {
    attempts++;
    if (scan() || attempts > 150) clearInterval(poll);
  }, 200);

  // Catch embeds added later (e.g. SPA-style page transitions in this theme).
  if (window.MutationObserver) {
    new MutationObserver(function () { scan(); })
      .observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState !== 'loading') scan();
  else document.addEventListener('DOMContentLoaded', scan);
})();
